import { inject } from '@angular/core';
import {
  IOnDemandCheckoutCompletionResponse,
  IOnDemandCheckoutAbortResponse,
  IOnDemandCheckoutProcessRequest,
  IOnDemandCheckoutProcessResponse,
  ON_DEMAND_CHECKOUT_COMPLETION_RESPONSE_SCHEMA,
  ON_DEMAND_CHECKOUT_ABORT_RESPONSE_SCHEMA,
  ON_DEMAND_CHECKOUT_PROCESS_RESPONSE_SCHEMA,
} from '@web/dtos';
import { API_RESPONSE_SCHEMA, IApiResponse } from '@web/shared-types';
import { Observable } from 'rxjs';
import { RestCallBuilder } from '../../../builder/rest/rest-call.builder';
import { AppConfigService } from '../../app-config.service';

export class OnDemandOrderGateway {
  private readonly configService: AppConfigService = inject(AppConfigService);
  private readonly startBuilder: RestCallBuilder<
    IOnDemandCheckoutProcessRequest,
    IOnDemandCheckoutProcessResponse
  > = new RestCallBuilder<
    IOnDemandCheckoutProcessRequest,
    IOnDemandCheckoutProcessResponse
  >({
    orderId: '',
    booking: { bookingRef: '', systemRef: '' },
    completed: false,
  });
  private readonly completionBuilder: RestCallBuilder<
    Record<string, never>,
    IOnDemandCheckoutCompletionResponse
  > = new RestCallBuilder<
    Record<string, never>,
    IOnDemandCheckoutCompletionResponse
  >({ completed: false });
  private readonly abortBuilder: RestCallBuilder<
    Record<string, never>,
    IOnDemandCheckoutAbortResponse
  > = new RestCallBuilder<
    Record<string, never>,
    IOnDemandCheckoutAbortResponse
  >({ cancelled: false });

  public startCheckout(
    request: IOnDemandCheckoutProcessRequest,
    guestCheckout: boolean
  ): Observable<IApiResponse<IOnDemandCheckoutProcessResponse>> {
    const path = this.getCheckoutPath(guestCheckout);
    return this.startBuilder
      .postExternal(
        `${this.configService.get().general.directTicketUrl}/orders/on-demand/${path}`,
        request
      )
      .validate(
        API_RESPONSE_SCHEMA(ON_DEMAND_CHECKOUT_PROCESS_RESPONSE_SCHEMA),
        'OnDemandOrder.startCheckout'
      )
      .build();
  }

  public completeCheckout(
    orderId: string,
    guestCheckout: boolean,
    customerReference: string
  ): Observable<IApiResponse<IOnDemandCheckoutCompletionResponse>> {
    const path = this.getOrderActionPath(
      orderId,
      customerReference,
      guestCheckout,
      'complete'
    );
    return this.completionBuilder
      .postExternal(
        `${this.configService.get().general.directTicketUrl}/orders/on-demand/${path}`,
        {}
      )
      .validate(
        API_RESPONSE_SCHEMA(ON_DEMAND_CHECKOUT_COMPLETION_RESPONSE_SCHEMA),
        'OnDemandOrder.completeCheckout'
      )
      .build();
  }

  public abortCheckout(
    orderId: string,
    guestCheckout: boolean,
    customerReference: string
  ): Observable<IApiResponse<IOnDemandCheckoutAbortResponse>> {
    const path = this.getOrderActionPath(
      orderId,
      customerReference,
      guestCheckout,
      'abort'
    );
    return this.abortBuilder
      .postExternal(
        `${this.configService.get().general.directTicketUrl}/orders/on-demand/${path}`,
        {}
      )
      .validate(
        API_RESPONSE_SCHEMA(ON_DEMAND_CHECKOUT_ABORT_RESPONSE_SCHEMA),
        'OnDemandOrder.abortCheckout'
      )
      .build();
  }

  private getCheckoutPath(guestCheckout: boolean): string {
    if (guestCheckout) {
      return 'guest/checkout';
    }
    return 'checkout';
  }

  private getOrderActionPath(
    orderId: string,
    customerReference: string,
    guestCheckout: boolean,
    action: 'complete' | 'abort'
  ): string {
    if (guestCheckout) {
      return `guest/${customerReference}/${orderId}/${action}`;
    }
    return `${orderId}/${action}`;
  }
}
